<template>
  <div class="application-status">
    <!-- Pending Status -->
    <div v-if="status === 'pending'" class="status-card">
      <div class="status-card__icon status-card__icon--warning">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="14" r="11" stroke="currentColor" stroke-width="2"/>
          <path d="M14 8V14L18 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h3 class="status-card__title">Your application is still in progress</h3>
      <p class="status-card__text">
        You have turned on the public raise feature. Kindly complete the due
        diligence form by clicking on the button below.
      </p>
      <button class="status-card__btn" @click="gotoForm" v-can.disable="'deal:addDeal'">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.75 11.25V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M5.25 7.5L9 11.25L12.75 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9 11.25V2.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Complete Due Diligence Form
      </button>
    </div>

    <!-- Review Status -->
    <div v-if="status === 'review'" class="status-card">
      <div class="status-card__icon status-card__icon--info">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="14" r="11" stroke="currentColor" stroke-width="2"/>
          <path d="M14 9.33334V14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14 18.6667H14.0117" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h3 class="status-card__title">Your application is being reviewed</h3>
      <p class="status-card__text">
        You have applied to be listed. Sit back and relax while we review your
        application. You will be able to list once your application has been
        approved.
      </p>
    </div>

    <!-- Approved Status -->
    <div v-if="status === 'approved'" class="status-card">
      <div class="status-card__icon status-card__icon--success">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M23.3333 7L10.5 19.8333L4.66666 14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h3 class="status-card__title">Your application was successful</h3>
      <p class="status-card__text">
        Congratulations, dear founder. Please click on the button below to
        create a public token and start raising.
      </p>
      <button class="status-card__btn" @click="createToken">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 3.75V14.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3.75 9H14.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Create Your Token
      </button>
    </div>

    <!-- Rejected Status -->
    <div v-if="status === 'rejected'" class="status-card">
      <div class="status-card__icon status-card__icon--error">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="14" r="11" stroke="currentColor" stroke-width="2"/>
          <path d="M17.5 10.5L10.5 17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10.5 10.5L17.5 17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h3 class="status-card__title">We could not accept your application</h3>
      <p class="status-card__text">
        Perhaps it was an issue with the details you provided, but be rest
        assured that someone on our support team will reach out to you.
      </p>
      <a href="mailto:support@getequity.io" class="status-card__btn-link">
        <button class="status-card__btn status-card__btn--outline">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3H15C15.825 3 16.5 3.675 16.5 4.5V13.5C16.5 14.325 15.825 15 15 15H3C2.175 15 1.5 14.325 1.5 13.5V4.5C1.5 3.675 2.175 3 3 3Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M16.5 4.5L9 9.75L1.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Send a Mail to Us
        </button>
      </a>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { getItem } from "@/utils/storage.js";

export default {
  name: "ApplicationStatus",

  props: {
    status: { type: String, default: () => "", required: true },
  },

  data() {
    return {
      assetId: "",
    };
  },

  computed: {
    ...mapState({
      assetDetails: (state) => state.assetModule.assetDetails,
    }),
  },

  created() {
    this.assetId = getItem("org_details").asset;
    if (this.assetId) this.getAsset(this.assetId);
  },

  methods: {
    ...mapActions("assetModule", ["getAsset"]),

    createToken() {
      if (this.assetId) {
        this.$router.push({
          name: "CreateAssetInfo",
          params: { type: "public-raise" },
          query: { asset: `${this.assetId}` },
        });
      } else {
        this.$router.push({
          name: "CreateAsset",
          params: { type: "public-raise" },
        });
      }
    },
    gotoForm() {
      window.open("https://airtable.com/shrNQr1FF3Io52cCO", "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
.application-status {
  max-width: 520px;
  margin: 0 auto;
}

.status-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  padding: 48px 32px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin-bottom: 24px;

    &--warning {
      background: #FEF3C7;
      color: #D97706;
    }

    &--info {
      background: #DBEAFE;
      color: #3B82F6;
    }

    &--success {
      background: #D1FAE5;
      color: #10B981;
    }

    &--error {
      background: #FEE2E2;
      color: #EF4444;
    }
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 12px 0;
  }

  &__text {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    line-height: 1.6;
    margin: 0 0 24px 0;
    max-width: 380px;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 28px;
    background: #4D84FF;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: #3B6EE8;
    }

    &--outline {
      background: transparent;
      border: 1px solid #E2E8F0;
      color: #0F172A;

      &:hover {
        background: #F8FAFC;
        border-color: #CBD5E1;
      }
    }
  }

  &__btn-link {
    text-decoration: none;
  }
}
</style>
